// ============================================================================
// Book a Delhivery REVERSE pickup for one return (customer → warehouse). Unlike the forward
// PO-booking modal (which quotes a PO and shows a vehicle picker), the reverse leg is booked by
// POSTing the row's server-authored `book_delhivery_href`; the backend quotes serviceability itself
// and auto-picks a vehicle, so every field here is an OPTIONAL override. On success the parent
// refreshes the row — the live Delhivery booking then shows in its logistics block and the button
// disappears. A 4xx surfaces `rejection_reason`; an already-booked 409 that carried a tracking link
// renders it so ops can jump to the existing booking.
// ============================================================================
import { Alert, Input, Modal, Space, Switch, Typography, message } from "antd";
import React, { useEffect, useState } from "react";
import ContactSelect from "../../logistics/contact_select";
import { bookReturnDelhivery, ReturnRow } from "../returns.api";

const { Text } = Typography;

const ReturnDelhiveryModal: React.FC<{
  row: ReturnRow | null;
  onClose: () => void;
  onBooked: () => void;
}> = ({ row, onClose, onBooked }) => {
  const [vehicleMode, setVehicleMode] = useState("");
  const [pickupContact, setPickupContact] = useState("");
  const [dropContact, setDropContact] = useState("");
  const [pickupOtp, setPickupOtp] = useState(false);
  const [dropOtp, setDropOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ message: string; trackingUrl?: string | null } | null>(null);

  // Reset on each open; prefill the pickup end with the customer's number (the warehouse drop defaults
  // server-side, so it starts blank).
  useEffect(() => {
    if (!row) return;
    setVehicleMode("");
    setPickupContact(row.client_contact ?? "");
    setDropContact("");
    setPickupOtp(false);
    setDropOtp(false);
    setError(null);
    setSubmitting(false);
  }, [row]);

  const submit = async () => {
    if (!row?.book_delhivery_href) return;
    setSubmitting(true);
    setError(null);
    const res = await bookReturnDelhivery(row.book_delhivery_href, {
      vehicle_mode: vehicleMode.trim() || undefined,
      pickup_contact_number: pickupContact.trim() || undefined,
      drop_contact_number: dropContact.trim() || undefined,
      pickup_otp_enabled: pickupOtp,
      drop_otp_enabled: dropOtp,
    });
    setSubmitting(false);
    if (res.status === "ok") {
      message.success("Reverse pickup booked with Delhivery");
      onBooked();
    } else {
      // Keep the modal open so the reason (and any tracking link) stays on screen.
      setError({ message: res.message, trackingUrl: res.trackingUrl });
      message.error(res.message);
    }
  };

  const otpRow = (
    label: string,
    help: string,
    checked: boolean,
    onChange: (v: boolean) => void
  ) => (
    <Space align="start" size={10}>
      <Switch checked={checked} onChange={onChange} />
      <div>
        <Text strong>{label}</Text>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {help}
          </Text>
        </div>
      </div>
    </Space>
  );

  return (
    <Modal
      title={row ? `Book Delhivery reverse pickup · R-${row.stage_id}` : "Book Delhivery"}
      open={!!row}
      onCancel={onClose}
      onOk={submit}
      okText="Book Delhivery"
      okButtonProps={{ loading: submitting }}
      destroyOnClose
      width={560}
    >
      <p style={{ marginTop: 0, color: "#888" }}>
        Books the reverse pickup (customer → warehouse) with Delhivery. Serviceability is checked
        first — a non-serviceable route won't be booked. All fields below are optional.
      </p>
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            Pickup contact (customer)
          </Text>
          <ContactSelect value={pickupContact} onChange={setPickupContact} />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            Drop contact (warehouse)
          </Text>
          <ContactSelect value={dropContact} onChange={setDropContact} />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            Vehicle (optional)
          </Text>
          <Input
            value={vehicleMode}
            onChange={(e) => setVehicleMode(e.target.value)}
            placeholder="Leave blank to auto-pick (e.g. 3-wheeler)"
            style={{ width: 260 }}
          />
        </div>
        {otpRow(
          "Require pickup OTP",
          "A one-time code at the customer's end to start the pickup.",
          pickupOtp,
          setPickupOtp
        )}
        {otpRow(
          "Require drop OTP",
          "A one-time code at the warehouse to complete the drop.",
          dropOtp,
          setDropOtp
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message="Couldn't book"
            description={
              <span>
                {error.message}
                {error.trackingUrl && (
                  <>
                    {" · "}
                    <a href={error.trackingUrl} target="_blank" rel="noopener noreferrer">
                      Track existing booking
                    </a>
                  </>
                )}
              </span>
            }
          />
        )}
      </Space>
    </Modal>
  );
};

export default ReturnDelhiveryModal;
